"use client";

import { useLayoutEffect } from "react";

/**
 * Context-menu blur (Dallas 2026-09-26): one light 2px backdrop-filter veil on
 * the rounded app card (.studio-polish), spanning its FULL height — including
 * behind the tab header, which is raised above the veil (z-index) so it stays
 * sharp. The gradient stage outside the card stays sharp too.
 * Only one veil exists no matter how many menus are open (ref-counted), and
 * nothing blurs the scroll layers themselves.
 *
 * 2026-09-27: the veil used to start at the header's bottom edge, which left a
 * sharp band above it. Two changes fix that, and they only work together:
 *  - the workspace head is z-indexed above the veil, so it stays sharp; and
 *  - the top seam is gone, so the veil runs the full card height.
 * Do not reintroduce a "head bottom" seam: the desktop head is position:absolute
 * with a small inset and legitimately renders a few px above the card box, so
 * any head-relative seam re-cuts the top of the blur.
 *
 * The nav seam stays: the phone bottom nav floats over the card and must stay
 * sharp, and it is NOT z-indexed above the veil.
 */
let holders = 0;

const NAV_SELECTOR = ".studio-mobile-bottom-nav";

/** Never the veil target: the boot frame and pane skeletons reuse the shell classes. */
const GHOST_CARD_SELECTOR = ".ys-boot-frame, .studio-pane-skeleton-root";

/**
 * The live app card the menu veil sits on. Not a bare
 * `querySelector(".studio-polish")`: the first-paint boot frame
 * (#ys-boot-frame, root layout, BEFORE the app in the DOM) keeps a skeleton
 * `.studio-polish` laid out (opacity 0, visibility hidden, fixed inset 0) for
 * the whole page load. On phones that ghost came first, so the veil was sized
 * to the skeleton card, which starts higher than the real one.
 */
export function studioMenuVeilCard(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  let fallback: HTMLElement | null = null;
  for (const card of document.querySelectorAll<HTMLElement>(".studio-polish")) {
    if (card.closest(GHOST_CARD_SELECTOR)) continue;
    if (card.parentElement?.classList.contains("studio-stage")) return card;
    fallback ??= card;
  }
  return fallback;
}

function visibleRects(card: HTMLElement, selector: string): DOMRect[] {
  const out: DOMRect[] = [];
  card.querySelectorAll<HTMLElement>(selector).forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return;
    if (r.bottom <= 0 || r.top >= window.innerHeight) return;
    out.push(r);
  });
  return out;
}

function applyVeil() {
  const card = studioMenuVeilCard();
  if (!card) return;
  const box = card.getBoundingClientRect();
  const mid = box.top + box.height / 2;
  // No top seam: the head is z-indexed above the veil, so the veil runs from
  // the card's top edge to its bottom (minus the phone nav, which floats and
  // must stay sharp).
  const top = 0;
  let bottom = 0;
  for (const r of visibleRects(card, NAV_SELECTOR)) {
    if (r.bottom > mid) bottom = Math.max(bottom, box.bottom - r.top);
  }
  card.style.setProperty("--studio-menu-blur-top", `${Math.max(0, Math.round(top))}px`);
  card.style.setProperty("--studio-menu-blur-bottom", `${Math.max(0, Math.round(bottom))}px`);
  card.classList.toggle("is-menu-blur-over-nav", bottom > 0);
  cancelVeilLeave(card);
  card.classList.add("is-menu-blur");
}

/** Veil fade-out length; matches `.is-menu-blur-leaving::before` in studio-files-chrome.css. */
const VEIL_EXIT_MS = 180;
let leaveTimer = 0;

function cancelVeilLeave(card: HTMLElement | null) {
  if (leaveTimer) window.clearTimeout(leaveTimer);
  leaveTimer = 0;
  card?.classList.remove("is-menu-blur-leaving");
}

function removeVeil(card: HTMLElement | null) {
  cancelVeilLeave(card);
  card?.classList.remove("is-menu-blur", "is-menu-blur-over-nav");
}

/** Close reverses the open: the veil fades back to unblurred, then the class goes. */
function clearVeil() {
  const card = studioMenuVeilCard();
  if (!card?.classList.contains("is-menu-blur")) return;
  const reduce =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduce) {
    removeVeil(card);
    return;
  }
  if (leaveTimer) return;
  card.classList.add("is-menu-blur-leaving");
  leaveTimer = window.setTimeout(() => {
    leaveTimer = 0;
    if (holders === 0) removeVeil(card);
  }, VEIL_EXIT_MS);
}

/**
 * The veil is applied two frames AFTER the menu mounts. Toggling it restyles
 * and re-layers the whole app card (class on .studio-polish, isolation,
 * backdrop-filter); doing that in the menu's layout pass held back the menu's
 * first paint, so every context menu felt slow to open. Now the menu paints
 * at once and the blur follows a frame later.
 */
let pendingVeil = 0;

function cancelPendingVeil() {
  if (pendingVeil) window.cancelAnimationFrame(pendingVeil);
  pendingVeil = 0;
}

export function acquireStudioMenuBlur(): () => void {
  if (typeof document === "undefined") return () => {};
  holders += 1;
  if (holders === 1) {
    // Reopened while the veil was fading out: keep it, no flash to sharp.
    cancelVeilLeave(studioMenuVeilCard());
    cancelPendingVeil();
    pendingVeil = window.requestAnimationFrame(() => {
      pendingVeil = window.requestAnimationFrame(() => {
        pendingVeil = 0;
        if (holders > 0) applyVeil();
      });
    });
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders = Math.max(0, holders - 1);
    if (holders === 0) {
      cancelPendingVeil();
      clearVeil();
    }
  };
}

/** Blur the app card while `active` (menu open and not leaving). */
export function useStudioMenuBlur(active: boolean) {
  useLayoutEffect(() => {
    if (!active) return;
    return acquireStudioMenuBlur();
  }, [active]);
}
